import { c as _c } from "react/compiler-runtime";
import type { ActivationSettings } from '@sizzlenz/shared';
import { ACTIVATION_ROLE_OPTIONS, INITIATIVE_TAG_OPTIONS, ACTIVATION_ENVIRONMENT_OPTIONS, ACTIVATION_AUDIENCE_OPTIONS, ACTIVATION_CAPABILITY_OPTIONS, ACTIVATION_TERMS_OPTIONS, ACTIVATION_REQUIREMENT_OPTIONS, ACTIVATION_AVAILABILITY_OPTIONS, ACTIVATION_PROMOTION_OPTIONS, PRODUCT_CATEGORY_OPTIONS } from '@sizzlenz/shared';
import { useGeoAreasNZ, resolveGeoAreaNames } from '@/lib/hooks/useGeoAreas';
import { Users, Zap, MapPin, UserCheck, Wrench, DollarSign, ShieldCheck, Clock, Megaphone, Tag, Maximize, Timer, Move, Footprints, Minimize } from 'lucide-react';
type OptionDef = readonly {
  id: string;
  label: string;
}[];
function resolveLabels(keys: string[] | undefined, options: OptionDef): string[] {
  if (!keys?.length) return [];
  const map = new Map(options.map(o => [o.id, o.label]));
  return keys.map(k => map.get(k) || k);
}
function resolveLabel(key: string | undefined, options: OptionDef): string | null {
  if (!key) return null;
  return options.find(o => o.id === key)?.label || key;
}
interface TagRowProps {
  icon: React.ReactNode;
  label: string;
  values: string[];
}
function TagRow(t0) {
  const $ = _c(14);
  const {
    icon,
    label,
    values
  } = t0;
  if (!values.length) {
    return null;
  }
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="text-muted-foreground shrink-0">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-sm text-muted-foreground">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== values) {
    t4 = values.map(_temp);
    $[7] = values;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <div className="flex flex-wrap gap-1 mt-0.5 ml-5.5">{t4}</div>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <div>{t3}{t5}</div>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp(v) {
  return <span key={v} className="inline-block rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground">{v}</span>;
}
interface MetricProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  unit?: string;
}
function Metric(t0) {
  const $ = _c(15);
  const {
    icon,
    label,
    value,
    unit
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="text-muted-foreground">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-sm text-muted-foreground">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== value) {
    t3 = value.toLocaleString();
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = unit ? ` ${unit}` : "";
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <p className="text-sm font-medium">{t3}{t4}</p>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t5) {
    t6 = <div>{t2}{t5}</div>;
    $[9] = t2;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t1 || $[13] !== t6) {
    t7 = <div className="flex items-center gap-2 rounded-md border px-3 py-2">{t1}{t6}</div>;
    $[12] = t1;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
interface ActivationSettingsSnapshotProps {
  settings: Partial<ActivationSettings>;
  className?: string;
}
export function ActivationSettingsSnapshot(t0) {
  const $ = _c(74);
  const {
    settings,
    className
  } = t0;
  const {
    regionsData
  } = useGeoAreasNZ();
  let t1;
  if ($[0] !== settings.activities) {
    t1 = resolveLabels(settings.activities, INITIATIVE_TAG_OPTIONS);
    $[0] = settings.activities;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activities = t1;
  let t2;
  if ($[2] !== settings.locations) {
    t2 = settings.locations || [];
    $[2] = settings.locations;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== regionsData || $[5] !== t2) {
    t3 = resolveGeoAreaNames(t2, regionsData);
    $[4] = regionsData;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const locations = t3;
  let t4;
  if ($[7] !== settings.environment) {
    t4 = resolveLabel(settings.environment, ACTIVATION_ENVIRONMENT_OPTIONS);
    $[7] = settings.environment;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const env = t4;
  let t5;
  if ($[9] !== settings.audienceProfile) {
    t5 = resolveLabels(settings.audienceProfile, ACTIVATION_AUDIENCE_OPTIONS);
    $[9] = settings.audienceProfile;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const audience = t5;
  let t6;
  if ($[11] !== settings.capabilities) {
    t6 = resolveLabels(settings.capabilities, ACTIVATION_CAPABILITY_OPTIONS);
    $[11] = settings.capabilities;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const capabilities = t6;
  let t7;
  if ($[13] !== settings.terms) {
    t7 = resolveLabels(settings.terms, ACTIVATION_TERMS_OPTIONS);
    $[13] = settings.terms;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const terms = t7;
  let t8;
  if ($[15] !== settings.requirements) {
    t8 = resolveLabels(settings.requirements, ACTIVATION_REQUIREMENT_OPTIONS);
    $[15] = settings.requirements;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const requirements = t8;
  let t9;
  if ($[17] !== settings.availability) {
    t9 = resolveLabels(settings.availability, ACTIVATION_AVAILABILITY_OPTIONS);
    $[17] = settings.availability;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const availability = t9;
  let t10;
  if ($[19] !== settings.promotionSupport) {
    t10 = resolveLabels(settings.promotionSupport, ACTIVATION_PROMOTION_OPTIONS);
    $[19] = settings.promotionSupport;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const promotion = t10;
  let t11;
  if ($[21] !== settings.preferredBrandCategories) {
    t11 = resolveLabels(settings.preferredBrandCategories, PRODUCT_CATEGORY_OPTIONS);
    $[21] = settings.preferredBrandCategories;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const brandCats = t11;
  const hasMetrics = settings.minAttendees || settings.maxAttendees || settings.typicalDuration || settings.spaceSizeM2 || settings.approxDailyFootTraffic;
  const hasAnyData = activities.length > 0 || locations.length > 0 || env || audience.length > 0 || capabilities.length > 0 || terms.length > 0 || requirements.length > 0 || availability.length > 0 || promotion.length > 0 || brandCats.length > 0 || hasMetrics;
  if (!hasAnyData) {
    return null;
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Zap className="w-3.5 h-3.5" />;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== activities) {
    t13 = <TagRow icon={t12} label="Activities" values={activities} />;
    $[24] = activities;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <MapPin className="w-3.5 h-3.5" />;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== locations) {
    t15 = <TagRow icon={t14} label="Locations" values={locations} />;
    $[27] = locations;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== env) {
    t16 = env && <TagRow icon={<MapPin className="w-3.5 h-3.5" />} label="Environment" values={[env]} />;
    $[29] = env;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <UserCheck className="w-3.5 h-3.5" />;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== audience) {
    t18 = <TagRow icon={t17} label="Audience" values={audience} />;
    $[32] = audience;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Wrench className="w-3.5 h-3.5" />;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== capabilities) {
    t20 = <TagRow icon={t19} label="Facilities / Equipment" values={capabilities} />;
    $[35] = capabilities;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <DollarSign className="w-3.5 h-3.5" />;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== terms) {
    t22 = <TagRow icon={t21} label="Terms" values={terms} />;
    $[38] = terms;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ShieldCheck className="w-3.5 h-3.5" />;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== requirements) {
    t24 = <TagRow icon={t23} label="Requirements" values={requirements} />;
    $[41] = requirements;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Clock className="w-3.5 h-3.5" />;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== availability) {
    t26 = <TagRow icon={t25} label="Availability" values={availability} />;
    $[44] = availability;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Megaphone className="w-3.5 h-3.5" />;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== promotion) {
    t28 = <TagRow icon={t27} label="Promotion Support" values={promotion} />;
    $[47] = promotion;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Tag className="w-3.5 h-3.5" />;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== brandCats) {
    t30 = <TagRow icon={t29} label="Preferred Product Categories" values={brandCats} />;
    $[50] = brandCats;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== hasMetrics || $[53] !== settings.approxDailyFootTraffic || $[54] !== settings.maxAttendees || $[55] !== settings.minAttendees || $[56] !== settings.spaceSizeM2 || $[57] !== settings.typicalDuration) {
    t31 = hasMetrics && <div className="grid grid-cols-2 gap-2 pt-1">{settings.minAttendees != null && <Metric icon={<Minimize className="w-3.5 h-3.5" />} label="Min Attendees" value={settings.minAttendees} unit="people" />}{settings.maxAttendees != null && <Metric icon={<Maximize className="w-3.5 h-3.5" />} label="Max Attendees" value={settings.maxAttendees} unit="people" />}{settings.typicalDuration != null && <Metric icon={<Timer className="w-3.5 h-3.5" />} label="Duration" value={settings.typicalDuration} unit="hrs" />}{settings.spaceSizeM2 != null && <Metric icon={<Move className="w-3.5 h-3.5" />} label="Space" value={settings.spaceSizeM2} unit={"m\xB2"} />}{settings.approxDailyFootTraffic != null && <Metric icon={<Footprints className="w-3.5 h-3.5" />} label="Daily Foot Traffic" value={settings.approxDailyFootTraffic} />}</div>;
    $[52] = hasMetrics;
    $[53] = settings.approxDailyFootTraffic;
    $[54] = settings.maxAttendees;
    $[55] = settings.minAttendees;
    $[56] = settings.spaceSizeM2;
    $[57] = settings.typicalDuration;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== t13 || $[60] !== t15 || $[61] !== t16 || $[62] !== t18 || $[63] !== t20 || $[64] !== t22 || $[65] !== t24 || $[66] !== t26 || $[67] !== t28 || $[68] !== t30 || $[69] !== t31) {
    t32 = <div className="space-y-3">{t13}{t15}{t16}{t18}{t20}{t22}{t24}{t26}{t28}{t30}{t31}</div>;
    $[59] = t13;
    $[60] = t15;
    $[61] = t16;
    $[62] = t18;
    $[63] = t20;
    $[64] = t22;
    $[65] = t24;
    $[66] = t26;
    $[67] = t28;
    $[68] = t30;
    $[69] = t31;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== className || $[72] !== t32) {
    t33 = <div className={className}>{t32}</div>;
    $[71] = className;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  return t33;
}